/**
 * AI players modelled on real Poli Poker players: the active players on the score sheets' LADDER tab,
 * plus Scott, Chloe, Mr Lim and Dad (Scott's list, 3 Nov 2026). Each has a style of their own,
 * worked out from their record (scripts/build-personas.mjs → personas.generated.json):
 *
 *   win rate             → how sharp they play (fewer loose choices, watches what others hold)
 *   score swings         → how much risk they take
 *   states for points    → knocking early to grab territory, or waiting
 *   score vs wins        → hunting big hands (taking the middle) or scoring steadily
 *
 * The sheets hold only places, scores and states per game, so this is a likeness of each record,
 * not of their actual card play. Every game draws a different group of them at random.
 */
import DATA from "./personas.generated.json" with { type: "json" };
import { HandCategory } from "./handEval.ts";
import type { Profile } from "./ai/config.ts";
import type { Rng } from "./rng.ts";

export interface Persona {
  id: string;
  name: string;
  /** A few words for the player: "sharp, big-hand hunter". */
  style: string;
  profile: Omit<Profile, "name" | "nearZ">;
  record: { games: number; wins: number; avgScore: number; statesPerGame: number; kings: number };
}

export const PERSONAS: Persona[] = DATA as Persona[];
const BY_ID = new Map(PERSONAS.map((p) => [p.id, p]));
export const persona = (id: string | null | undefined): Persona | undefined => (id ? BY_ID.get(id) : undefined);

/** The smart AI's profile for a persona (solo games). */
export function personaProfile(p: Persona): Profile {
  return { name: p.id, nearZ: 1.0, ...p.profile };
}

/** How the simple AI (multiplayer, and anywhere CPU is tight) plays. The defaults are the plain simple AI. */
export interface SimpleStyle {
  /** Knock straight away holding this or better. */
  knockAt: HandCategory;
  /** Knock on two pair from this turn on. */
  pairKnockTurn: number;
  /** Take the whole (face-up) middle when it's at least this and beats our hand. */
  takeMiddleAt: HandCategory;
  /** Keep drawing blind up to this many turns. */
  blindTurns: number;
  /** Chance of a blind first-turn take with nothing in hand. */
  blindGamble: number;
  /** Chance of passing up an improving swap for a blind draw (before any knock) — a looser player. */
  slip: number;
}
export const PLAIN_STYLE: SimpleStyle = { knockAt: HandCategory.ThreeOfAKind, pairKnockTurn: 1, takeMiddleAt: HandCategory.Straight, blindTurns: 6, blindGamble: 0.45, slip: 0 };

export function simpleStyle(p: Persona | undefined): SimpleStyle {
  if (!p) return PLAIN_STYLE;
  const { knockBias: k, takeMiddleBias: t, riskPreference: r, exploration: e } = p.profile;
  return {
    knockAt: k < -0.3 ? HandCategory.Straight : HandCategory.ThreeOfAKind,
    pairKnockTurn: k > 0.3 ? 0 : k < -0.3 ? 3 : 1,
    takeMiddleAt: t > 0.4 ? HandCategory.ThreeOfAKind : t < -0.3 ? HandCategory.Flush : HandCategory.Straight,
    blindTurns: r < -0.5 ? 8 : r > 0.5 ? 4 : 6,
    blindGamble: r < -0.5 ? 0.6 : r > 0.5 ? 0.25 : 0.45,
    slip: Math.max(0, Math.min(0.2, (e - 0.15) * 0.5)),
  };
}

/** A different group each game: `n` personas at random, leaving out anyone whose name is already at the
 *  table (a person never plays their own likeness, and nobody appears twice). */
export function pickPersonas(n: number, rng: Rng, exclude: string[] = []): Persona[] {
  const taken = new Set(exclude.map(norm));
  return rng.shuffle(PERSONAS.filter((p) => !taken.has(norm(p.name)))).slice(0, n);
}
const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z]/g, "");
